import React, { SyntheticEvent, useCallback, useEffect, useState } from "react";
import cn from "classnames";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import store from "store";
import { useIntl } from "react-intl";

import { battleOfTheHunsId, christmasId, getPlayItems } from "../../../../common/parameters";
import { setItem } from "../../../cookie";

import { IUser } from "../../../store/user/models";
import { IStateTree } from "../../../store/interfaces";
import routing from "../../../routing";
import Icon from "../../../icon";

import Tooltip from "../../Tooltip";

import { StyledQuickButtons, ContextWrapper } from "./styles";
import messages from "./messages";
import { IProps } from "./interfaces";
import ContextMenu from "../../ContextMenu";

const QuickButtons = ({
    isMobile,
    tiny,
    hover,
    mobilScroll,
    homeworkResult,
    browse,
    plan,
    homework,
    classView,
    moderation,
    privateView,
    studentResults,
}: IProps) => {
    const user = useSelector((state: IStateTree) => state.user) as IUser;
    const { formatMessage } = useIntl();
    const [openContext, setOpenContext] = useState<string | null>(null);
    const isExtra = plan._id === battleOfTheHunsId || plan._id === christmasId;
    const isChristmas = plan._id === christmasId;
    const handleClickWindow = useCallback(() => {
        window.removeEventListener("click", handleClickWindow);
        setOpenContext(null);
    }, [setOpenContext]);
    const handlePlayClick = (e: SyntheticEvent) => {
        if (plan.experience?.enabled) {
            e.preventDefault();
            if (openContext === "play") return;
            setOpenContext("play");
            setTimeout(() => {
                window.addEventListener("click", handleClickWindow);
            });
        } else {
            store.set("play-plan-id", plan._id);
        }
    };

    useEffect(() => () => {
        window.removeEventListener("click", handleClickWindow);
    }, []);

    useEffect(() => {
        if (!hover && openContext !== null) {
            handleClickWindow();
        }
    }, [hover]);
    return (
        <StyledQuickButtons tinyMobile={!!isMobile && !!tiny} tiny={!!tiny} className={cn((hover || mobilScroll) && "Show")}>
            {(privateView || !browse) && !homework && !moderation && (
                <>
                    <ContextWrapper key="play">
                        <Tooltip label={formatMessage(messages.playTest)} disabled={!plan.playable || openContext === "play"} light>
                            <Link
                                to={
                                    "/" +
                                    (isChristmas
                                        ? routing.christmasEdition
                                        : isExtra
                                        ? routing.battleOfTheHuns
                                        : (tiny ? routing.publicPlay : routing.play) + "/" + plan._id)
                                }
                                className={cn(!tiny && !plan.playable && "QuickButtonDisabled")}
                                onClick={handlePlayClick}
                            >
                                <Icon i="playlarge" />
                            </Link>
                        </Tooltip>
                        {openContext === "play" && (
                            <ContextMenu
                                getItems={getPlayItems}
                                planId={plan._id}
                                publicView={tiny}
                                backgroundColor="snow"
                                top={tiny ? 60 : 84}
                                mobileTop={84}
                            />
                        )}
                    </ContextWrapper>
                    {!tiny && (
                        <Tooltip
                            key="whiteboard"
                            label={formatMessage(messages.playShareTest)}
                            disabled={!plan.shared}
                            marginTop={12}
                            light
                        >
                            <Link
                                to={"/" + routing.share + "/" + plan.publicId}
                                className={cn(!plan.shared && "QuickButtonDisabled")}
                            >
                                <Icon i="whiteboard" />
                            </Link>
                        </Tooltip>
                    )}
                    {!tiny && (
                        <Tooltip key="edit" label={formatMessage(messages.edit)} light>
                            <Link to={"/" + routing.plan + "/" + plan._id}>
                                <Icon i="edit" />
                            </Link>
                        </Tooltip>
                    )}
                    {tiny && (
                        <Tooltip key="view" label={formatMessage(messages.edit)} light disabled>
                            <Link to={"/" + routing.publicPlan + "/" + plan._id}>
                                <Icon i="view" />
                            </Link>
                        </Tooltip>
                    )}
                </>
            )}
            {browse &&
                !privateView && [
                    <ContextWrapper key="play">
                        <Tooltip label={formatMessage(messages.playTest)} light disabled={tiny || openContext === "play"}>
                            <Link
                                to={
                                    "/" +
                                    (isChristmas
                                        ? routing.christmasEdition
                                        : isExtra
                                        ? routing.battleOfTheHuns
                                        : routing.publicPlay + "/" + plan._id)
                                }
                                onClick={handlePlayClick}
                            >
                                <Icon i="playlarge" />
                            </Link>
                        </Tooltip>
                        {openContext === "play" && (
                            <ContextMenu
                                getItems={getPlayItems}
                                publicView
                                planId={plan._id}
                                backgroundColor="snow"
                                top={84}
                                mobileTop={84}
                            />
                        )}
                    </ContextWrapper>,
                    !isExtra && !tiny && (
                        <Tooltip
                            key="whiteboard"
                            label={formatMessage(
                                user.loggedIn
                                    ? user.profiled
                                        ? messages.playShareTest
                                        : messages.profileToGadgets
                                    : messages.logInToGadgets
                            )}
                            marginTop={12}
                            light={user.loggedIn}
                            grey={!user.loggedIn}
                        >
                            {user.loggedIn ? (
                                <Link
                                    to={
                                        user.profiled
                                            ? "/" + routing.share + "/" + plan._id
                                            : "/" + routing.signupProfile
                                    }
                                >
                                    <Icon i="whiteboard" style={{ marginTop: 18 }}/>
                                </Link>
                            ) : (
                                <Icon i="whiteboard" style={{ margin: 12, marginTop: 18 }}/>
                            )}
                        </Tooltip>
                    ),
                    ((!isMobile && tiny) || !tiny) && (
                        <Tooltip key="view" label={formatMessage(messages.view)} light disabled={tiny}>
                            <Link to={"/" + routing.publicPlan + "/" + plan._id}>
                                <Icon i="view" />
                            </Link>
                        </Tooltip>
                    ),
                ]}
            {homework && [
                plan.date! >= Date.now() && <Tooltip key="play" label={formatMessage(messages.homeworkPlay)} light>
                    <Link
                        to={"/" + routing.homework + "/" + plan._id}
                        onClick={() => {
                            setItem("homeworkPlanId", plan._id, 60 * 60 * 24, "/");
                        }}
                    >
                        <Icon i="playlarge" />
                    </Link>
                </Tooltip>,
                user.loggedIn && user.isStudent && studentResults && <Tooltip key="view2" label={formatMessage(messages.view)} light>
                    <Link
                        to={"/" + routing.homeworkStudentResults + "/" + plan._id}
                    >
                        <Icon i="view" />
                    </Link>
                </Tooltip>,
                classView && (
                    <Tooltip key="view" label={formatMessage(messages.view)} light>
                        <Link to={"/" + routing.homeworkResult + "/" + plan._id}>
                            <Icon i="view" />
                        </Link>
                    </Tooltip>
                ),
                homeworkResult && (
                    <Tooltip key="original" label={formatMessage(messages.originalPlan)} light>
                        <Link to={"/" + routing.publicPlan + "/" + plan.planId}>
                            <Icon i="browse" />
                        </Link>
                    </Tooltip>
                ),
            ]}
            {moderation && (
                <Tooltip key="view" label={formatMessage(messages.moderate)} light>
                    <Link to={"/" + routing.testModeration + "/" + plan._id}>
                        <Icon i="view" />
                    </Link>
                </Tooltip>
            )}
        </StyledQuickButtons>
    );
};

export default QuickButtons;
